/**
 * Helpers for reading and persisting a user's per-cabinet view preferences.
 *
 * Preferences are stored per user, per cabinet, on the user's content:
 *  - `cabinetColumns[cabinetIdentifier].addedColumnIndexes` - ordered added columns
 *  - `cabinetFilters[cabinetIdentifier].addedFilterIndexes` - ordered added filters
 *  - `cabinetSort[cabinetIdentifier]` - the active row sort ([{ direction, index }])
 *
 * This mirrors how saved filter views persist on `User.content.savedViews`, so a
 * user's customized columns and sort are restored on any browser or device they
 * log in from (web only).
 */

import { getJSONItem, removeItem } from './localStorage';
const CABINET_COLUMNS_KEY = 'cabinetColumns';
const ADDED_COLUMN_INDEXES_KEY = 'addedColumnIndexes';
const CABINET_FILTERS_KEY = 'cabinetFilters';
const ADDED_FILTER_INDEXES_KEY = 'addedFilterIndexes';
const CABINET_SORT_KEY = 'cabinetSort';
const VALID_SORT_DIRECTIONS = new Set(['asc', 'desc']);

/**
 * Content path used with the user `change` action to persist an ordered list of
 * keys the user added to a cabinet.
 */
const addedKeysChangePath = (sectionKey, cabinetIdentifier, listKey) => `/content/${sectionKey}/${cabinetIdentifier}/${listKey}`;

/**
 * Ordered list of keys the user added to a cabinet, read from user content. Empty
 * when nothing is stored or the stored value is invalid, so callers fall back to
 * the cabinet's own definitions.
 */
const readAddedKeys = (content, sectionKey, cabinetIdentifier, listKey) => {
  var _content$sectionKey;
  const stored = content === null || content === void 0 || (_content$sectionKey = content[sectionKey]) === null || _content$sectionKey === void 0 || (_content$sectionKey = _content$sectionKey[cabinetIdentifier]) === null || _content$sectionKey === void 0 ? void 0 : _content$sectionKey[listKey];
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter(key => typeof key === 'string');
};
export const cabinetColumnsChangePath = cabinetIdentifier => addedKeysChangePath(CABINET_COLUMNS_KEY, cabinetIdentifier, ADDED_COLUMN_INDEXES_KEY);
export const cabinetFiltersChangePath = cabinetIdentifier => addedKeysChangePath(CABINET_FILTERS_KEY, cabinetIdentifier, ADDED_FILTER_INDEXES_KEY);

/**
 * Build the content path used with the user `change` action to persist the row
 * sort for a cabinet.
 */
export const cabinetSortChangePath = cabinetIdentifier => `/content/${CABINET_SORT_KEY}/${cabinetIdentifier}`;

/**
 * Legacy browser-localStorage key used before per-user server persistence.
 */
export const legacyColumnsStorageKey = (customer, cabinetIdentifier) => `tru_${customer}_${cabinetIdentifier}_columns`;

/**
 * Read added column keys from legacy localStorage (pre-server persistence).
 */
export const getLegacyAddedColumns = (customer, cabinetIdentifier) => {
  const stored = getJSONItem(legacyColumnsStorageKey(customer, cabinetIdentifier), {});
  const {
    addedColumnIndexes
  } = stored;
  if (!Array.isArray(addedColumnIndexes)) {
    return [];
  }
  return addedColumnIndexes.filter(key => typeof key === 'string');
};

/**
 * Remove legacy localStorage column preferences after migrating to user content.
 */
export const clearLegacyAddedColumns = (customer, cabinetIdentifier) => {
  removeItem(legacyColumnsStorageKey(customer, cabinetIdentifier));
};
export const getAddedColumnsFromContent = (content, cabinetIdentifier) => readAddedKeys(content, CABINET_COLUMNS_KEY, cabinetIdentifier, ADDED_COLUMN_INDEXES_KEY);
export const getAddedFiltersFromContent = (content, cabinetIdentifier) => readAddedKeys(content, CABINET_FILTERS_KEY, cabinetIdentifier, ADDED_FILTER_INDEXES_KEY);

/**
 * Read the persisted row sort for a cabinet from user content. Returns an empty
 * array when nothing is stored or the stored value is invalid, so callers fall
 * back to the cabinet's default ordering.
 */
export const getSortFromContent = (content, cabinetIdentifier) => {
  var _content$CABINET_SORT;
  const stored = content === null || content === void 0 || (_content$CABINET_SORT = content[CABINET_SORT_KEY]) === null || _content$CABINET_SORT === void 0 ? void 0 : _content$CABINET_SORT[cabinetIdentifier];
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter(entry => entry && typeof entry.index === 'string' && typeof entry.direction === 'string').map(entry => {
    const direction = entry.direction.toLowerCase();
    return VALID_SORT_DIRECTIONS.has(direction) ? {
      direction,
      index: entry.index
    } : null;
  }).filter(Boolean);
};

/**
 * Filter a sort array to indexes that are still valid for the cabinet columns.
 */
export const getRestorableSort = (sort, columnDefs = []) => {
  if (!Array.isArray(sort) || sort.length === 0) {
    return [];
  }
  const validSortIndexes = new Set(columnDefs.filter(col => col.sortIndex).map(col => col.sortIndex));
  return sort.filter(entry => validSortIndexes.has(entry.index));
};